import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import * as RegistryConstants from 'constants/RegistryConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {BioentityInfo} from 'folder-items/FileConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {RegistrationStructuredTableMetadata} from 'notebook/integrations/registry/RegistrationTableConstants';
import {
  ALIAS_COLUMN_NAME,
  BIOENTITY_NAME_COLUMN_NAME,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import filterOutColumnTypes from 'notebook/structured-tables/columns/filterOutColumnTypes';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {getEnabledColumnAdapters} from 'notebook/structured-tables/StructuredTableColumnAdapters';
import {
  DirectLookupInfo,
  PrimarySample,
  PrimarySampleLookupType,
  StructuredTableColumn,
  TagValueStructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {getBioentityInfoFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {isTagSchema} from 'schemas/SchemaPredicates';

import {transformColumnsForInterfaces} from './SchemaInterfaceColumnTransformerBase';

export function defaultStructuredTableColumnsForRegisterBioentity({
  baseColumns,
  bioentityExists,
  entityNameColumnConfiguration,
  implementedSchemaInterfaces,
}: {
  baseColumns: Array<StructuredTableColumn>;
  bioentityExists: boolean;
  entityNameColumnConfiguration: 'REQUIRED' | 'READONLY' | 'ABSENT';
  implementedSchemaInterfaces?: Array<ImplementedStaticSchemaInterfaceGQL>;
}): Array<StructuredTableColumn> {
  const bioentityNameColumn =
    entityNameColumnConfiguration !== 'ABSENT'
      ? [
          makeStructuredTableColumn({
            columnType: TableColumnType.BIOENTITY_NAME,
            name: BIOENTITY_NAME_COLUMN_NAME,
            isReadOnly: entityNameColumnConfiguration === 'READONLY',
            isRequired: entityNameColumnConfiguration !== 'READONLY',
          }),
        ]
      : [];

  const aliasColumn = [
    makeStructuredTableColumn({
      columnType: TableColumnType.ALIAS,
      name: ALIAS_COLUMN_NAME,
    }),
  ];

  const columns = baseColumns.flatMap((column) => {
    if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
      const existingBioentityColumn = {
        ...column,
        isReadOnly: !bioentityExists,
        isRequired: bioentityExists,
      };
      return [existingBioentityColumn, ...bioentityNameColumn, ...aliasColumn];
    } else {
      return column;
    }
  });
  if (implementedSchemaInterfaces != null && implementedSchemaInterfaces.length > 0) {
    return transformColumnsForInterfaces(columns, implementedSchemaInterfaces ?? [], {bioentityExists});
  }
  return columns;
}

function lookupConvertedFromLinkIfPresent(column: StructuredTableColumn): boolean {
  if (column.hasOwnProperty('schemaFieldIsConvertedFromLink')) {
    return (column as TagValueStructuredTableColumn).schemaFieldIsConvertedFromLink ?? false;
  }
  return false;
}

/**
 * Filter columns that are not visible in registration tables by default.
 *
 * This includes: Alias, read only name, 'converted from' schema fields, and other platform columns.
 */
export function filterOutNonDefaultColumns(
  columns: Array<StructuredTableColumn>
): Array<StructuredTableColumn> {
  return filterOutColumnTypes(
    columns,
    [TableColumnType.ALIAS].concat(getEnabledColumnAdapters().map((adapter) => adapter.columnType))
  )
    .filter((column) => !lookupConvertedFromLinkIfPresent(column))
    .filter(({columnType, isReadOnly}) => !(columnType === TableColumnType.BIOENTITY_NAME && isReadOnly));
}

export function getBioentityPrimarySamplesFromStructuredTable(
  structuredTable: UnknownStructuredTable,
  computedTable?: Array<Array<EvaluatedCell>> | null,
  finalizedOnly?: boolean
): {[rowId: string]: Array<PrimarySample>} {
  const bioentityColumnIndex = structuredTable.columns.findIndex(
    ({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY
  );
  assert(
    bioentityColumnIndex !== -1,
    'Expected getBioentityPrimarySamplesFromStructuredTable to only be used on structured tables that have an EXISTING_BIOENTITY column'
  );
  const primarySamplesByRowId = {} as {
    [rowId: string]: Array<PrimarySample>;
  };

  const schema = getTagSchemaForStructuredTable(structuredTable);
  if (schema == null) {
    // The user doesn't have permissions on the schema
    structuredTable.rows.forEach(({rowId, cells, isFinalized}, rowIndex) => {
      if (!isFinalized && finalizedOnly) {
        return;
      }
      primarySamplesByRowId[rowId] = [];
    });
    return primarySamplesByRowId;
  }

  const schemaIdAndColumnGroup = structuredTable.metadata.schemaMetadata.find(
    ({schemaId}) => schemaId === schema.id
  );
  structuredTable.rows.forEach(({rowId, cells, isFinalized}, rowIndex) => {
    if (!isFinalized && finalizedOnly) {
      return;
    }
    // Bias towards the computedTable if available
    const typedValue =
      computedTable != null
        ? computedTable[rowIndex][bioentityColumnIndex]?.value
        : cells[bioentityColumnIndex].typedValue;
    const bioentity: BioentityInfo | null = typedValue ? getBioentityInfoFromTypedValue(typedValue) : null;
    if (bioentity) {
      const bioentitySample: PrimarySample = {
        schemaId: schema?.id,
        columnGroup: schemaIdAndColumnGroup?.group ?? null,
        sampleLookupInfo: {
          sampleType: PrimarySampleLookupType.DIRECT,
          idForLookup: bioentity.id,
        },
      };
      if (structuredTable.metadata.hasOwnProperty('customSyntaxName')) {
        (bioentitySample.sampleLookupInfo as DirectLookupInfo).customSyntaxName = (
          structuredTable.metadata as unknown as RegistrationStructuredTableMetadata
        ).customSyntaxName;
      }
      primarySamplesByRowId[rowId] = [bioentitySample];
    } else {
      primarySamplesByRowId[rowId] = [];
    }
  });
  return primarySamplesByRowId;
}

export function getTagSchemaForStructuredTable(structuredTable: UnknownStructuredTable): TagSchema | null {
  let tagSchema;
  const tagSchemas = structuredTable.schemas.filter((schema): schema is RegistryConstants.TagSchema =>
    isTagSchema(schema)
  );
  if (tagSchemas.length === 0) {
    // Schema is inaccessible or nonexistent
    tagSchema = null;
  } else if (tagSchemas.length === 1) {
    tagSchema = tagSchemas[0];
  } else {
    assert(false, `Expected ${structuredTable.kind} structured table to have a single bioentity schema`);
  }
  return tagSchema;
}

// NOTE: There is NO reason to add to this map. This is only needed for existing Registration Tables
// before we added the new `tagSchemaItemType` metadata key (around 2025-07-21).
const SSI_ID_TO_TAG_SCHEMA_ITEM_TYPE: {[ssiId: string]: TagSchemaItemType} = {
  [ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID]: TagSchemaItemType.OLIGO_CONJUGATE,
  [ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID]: TagSchemaItemType.OLIGO_DUPLEX,
  [ANTIBODY_SCHEMA_INTERFACE_ID]: TagSchemaItemType.ANTIBODY,
};

/**
 * Infers the TagSchemaItemType from metadata or by mapping implemented schema interfaces to known types.
 * Returns null if no TagSchemaItemType can be determined.
 */
export function inferTagSchemaItemTypeFromMetadata(
  metadata: Pick<RegistrationStructuredTableMetadata, 'tagSchemaItemType' | 'implementedSchemaInterfaces'>
): TagSchemaItemType | null {
  if (metadata.tagSchemaItemType) {
    // Found in metadata
    return metadata.tagSchemaItemType;
  }
  // Check if any implemented schema interfaces map to known types
  const mappedConfig = metadata.implementedSchemaInterfaces?.find(
    (c) => SSI_ID_TO_TAG_SCHEMA_ITEM_TYPE[c.schemaInterface.id]
  );
  if (mappedConfig) {
    // Inferred from implemented schema interfaces
    return SSI_ID_TO_TAG_SCHEMA_ITEM_TYPE[mappedConfig.schemaInterface.id];
  }
  // Not found
  return null;
}
